import { useState } from 'react';
import { ArrowRight, CreditCard, FileText, HelpCircle, Shield, CheckCircle, Phone, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import AppointmentModal from '../components/AppointmentModal';

const INSURANCE_FAQS = [
  {
    question: 'Do you accept dental insurance?',
    answer: 'Yes, we work with most dental insurance plans. We recommend contacting us with your insurance information so we can verify your coverage and help you understand your benefits.'
  },
  {
    question: 'What if I don\'t have insurance?',
    answer: 'We offer competitive self-pay rates. Our direct care model often results in lower overall costs compared to traditional multi-provider paths, even without insurance.'
  },
  {
    question: 'How much do dentures cost?',
    answer: 'Costs vary depending on the type of denture and complexity of your case. We provide detailed cost estimates during your consultation, with no hidden fees.'
  },
  {
    question: 'What forms of payment do you accept?',
    answer: 'We accept cash, personal checks, and all major credit cards including Visa, Mastercard, American Express, and Discover.'
  },
  {
    question: 'What is covered by insurance?',
    answer: 'Coverage varies by plan, but many dental insurance plans cover a portion of denture costs. We\'ll help you understand your specific coverage and maximize your benefits.'
  },
];

const PAYMENT_OPTIONS = [
  {
    icon: CreditCard,
    title: 'Credit Cards',
    description: 'We accept all major credit cards including Visa, Mastercard, American Express, and Discover.'
  },
  {
    icon: Banknote,
    title: 'Cash & Checks',
    description: 'We accept cash and personal checks for your convenience.'
  },
  {
    icon: Shield,
    title: 'Insurance Filing',
    description: 'We work with your insurance company to maximize your benefits and minimize out-of-pocket costs.'
  },
  {
    icon: HelpCircle,
    title: 'Free Consultation',
    description: 'Get a detailed cost estimate with no obligation. We believe in transparent pricing.'
  },
];

const PROCESS_STEPS = [
  {
    title: 'Contact Us',
    description: 'Call or submit an online inquiry with your insurance information.'
  },
  {
    title: 'Verification',
    description: 'We verify your coverage and explain your benefits.'
  },
  {
    title: 'Cost Estimate',
    description: 'Receive a detailed breakdown of costs and payment options.'
  },
  {
    title: 'Treatment',
    description: 'Begin your treatment with clear understanding of all costs.'
  },
];

export default function InsurancePaymentPage() {
  const [showAppointment, setShowAppointment] = useState(false);

  return (
    <div className="pt-24 lg:pt-32" data-testid="insurance-payment-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Insurance & Payment Options
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            We work with most insurance plans and offer flexible payment options to make quality denture care accessible.
          </p>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-24 bg-white" data-testid="payment-options-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Options
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Multiple ways to make your treatment affordable
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {PAYMENT_OPTIONS.map((option, idx) => (
              <div key={idx} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                  <option.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {option.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600">
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Process */}
      <section className="py-24 bg-slate-900 text-white" data-testid="insurance-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Insurance Process Made Simple
              </h2>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                We handle the insurance paperwork so you can focus on your treatment. Our team is experienced in working with dental insurance providers.
              </p>
              <div className="mt-10 space-y-8">
                {PROCESS_STEPS.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{step.title}</h3>
                      <p className="mt-1 text-slate-400">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800 rounded-3xl p-8 lg:p-12">
              <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Contact Us About Insurance
              </h3>
              <p className="text-slate-400 mb-8">
                Have questions about your coverage? We're here to help you understand your benefits.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-300">
                  <Phone className="w-5 h-5 text-orange-500" />
                  <span>(541) 756-2121</span>
                </div>
                <p className="text-sm text-slate-500">
                  Have your insurance card ready when you call, and we'll verify your coverage during the call.
                </p>
              </div>
              <Button
                onClick={() => setShowAppointment(true)}
                className="mt-8 w-full bg-orange-600 hover:bg-orange-700 rounded-full"
              >
                Schedule Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white" data-testid="insurance-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Insurance & Payment FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {INSURANCE_FAQS.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="bg-slate-50 rounded-xl border-none">
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline" data-testid={`insurance-faq-${idx}`}>
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="insurance-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Questions About Coverage?
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Contact us today and we'll help you understand your insurance benefits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Button
              onClick={() => setShowAppointment(true)}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg"
              data-testid="insurance-schedule-btn"
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-orange-600 rounded-full px-8 py-6 text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              (541) 756-2121
            </Button>
          </div>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
